import { filter, groupBy, map, orderBy, size, sortBy, sumBy } from "lodash";
import moment from "moment";

// Reporte "Tiempos de Cocina": cuánto tarda cada área desde que entra el
// pedido (created_at) hasta que cocina toca "Listo" (ready_at, lo guarda el
// backend al pasar a READY). Solo cuentan los pedidos que pasaron por la
// pantalla de Cocina; los que el mozo entregó directo (sin ready_at) se
// cuentan aparte como "servidos sin pasar por cocina".

// Umbrales iguales a los colores de la pantalla de Cocina (KitchenScreen.js).
export const PREP_WARN_MINUTES = 10;
export const PREP_LATE_MINUTES = 20;
// Un plato necesita al menos esta cantidad de pedidos para entrar al ranking
// de los más lentos (con 1–2 el promedio no dice nada).
export const SLOW_PRODUCT_MIN_SAMPLES = 3;
const SLOW_PRODUCTS_LIMIT = 10;
export const NO_AREA = "Sin área";

const getArea = (order) =>
  order.product_data?.category_data?.production_area?.trim() || NO_AREA;

export const prepMinutes = (order) =>
  (moment(order.ready_at).valueOf() - moment(order.created_at).valueOf()) / 60000;

function percentile(sortedValues, fraction) {
  if (sortedValues.length === 0) return 0;
  const index = Math.min(
    sortedValues.length - 1,
    Math.ceil(fraction * sortedValues.length) - 1
  );
  return sortedValues[Math.max(0, index)];
}

function summarize(orders) {
  const minutes = sortBy(map(orders, prepMinutes));
  const count = minutes.length;
  return {
    count,
    average: count ? sumBy(minutes) / count : 0,
    median: percentile(minutes, 0.5),
    // "9 de cada 10 salen en menos de X min" — más útil que el máximo, que
    // lo dispara un solo pedido olvidado.
    p90: percentile(minutes, 0.9),
    max: count ? minutes[count - 1] : 0,
    onTime: count
      ? (filter(minutes, (value) => value < PREP_WARN_MINUTES).length / count) * 100
      : 0,
    late: count
      ? (filter(minutes, (value) => value >= PREP_LATE_MINUTES).length / count) * 100
      : 0,
  };
}

export function formatMinutes(value) {
  if (value < 1) return "< 1 min";
  if (value < 60) return `${Math.round(value)} min`;
  const hours = Math.floor(value / 60);
  const rest = Math.round(value % 60);
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

export function buildPrepTimesReport(orders) {
  // product_data puede ser null (producto borrado): ver CLAUDE.md.
  const valid = filter(orders, (order) => order.product_data);
  const timed = filter(
    valid,
    (order) => order.ready_at && prepMinutes(order) >= 0
  );
  // Solo desde el primer "Listo" registrado: los pedidos de antes de que el
  // local empezara a usar la pantalla de Cocina tampoco tienen ready_at, y
  // contarlos como "sin pasar por cocina" daría un número engañoso.
  const firstTimed = timed.length
    ? Math.min(...map(timed, (order) => moment(order.created_at).valueOf()))
    : Infinity;
  const skippedKitchen = filter(
    valid,
    (order) =>
      !order.ready_at &&
      order.status === "DELIVERED" &&
      moment(order.created_at).valueOf() >= firstTimed
  );
  const skippedByArea = groupBy(skippedKitchen, getArea);

  const areas = orderBy(
    map(groupBy(timed, getArea), (areaOrders, area) => ({
      area,
      ...summarize(areaOrders),
      skipped: size(skippedByArea[area]),
    })),
    ["count"],
    ["desc"]
  );

  const slowestProducts = orderBy(
    filter(
      map(groupBy(timed, "product"), (productOrders) => ({
        title: productOrders[0].product_data.title,
        area: getArea(productOrders[0]),
        ...summarize(productOrders),
      })),
      (row) => row.count >= SLOW_PRODUCT_MIN_SAMPLES
    ),
    ["average"],
    ["desc"]
  ).slice(0, SLOW_PRODUCTS_LIMIT);

  return {
    overall: summarize(timed),
    areas,
    slowestProducts,
    skippedCount: size(skippedKitchen),
  };
}
